import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Photo } from '../types';

export interface AvatarSelection {
    url: string;
    timestamp: number;
    seed: string;
}

interface AvatarSelectionState {
    selections: Record<string, AvatarSelection>;
    rotationPeriod: number; // milliseconds
    hasHydrated: boolean;
    setAvatarSelection: (eventKey: string, url: string, seed: string) => void;
    selectOrCreateAvatarSelection: (eventKey: string, photos: Photo[], defaultUrl: string) => string;
    clearExpiredSelections: () => void;
    setRotationPeriod: (period: number) => void;
}

const DEFAULT_ROTATION_PERIOD = 24 * 60 * 60 * 1000;

// Seeded random function for consistent, deterministic results
const seededIndex = (seed: string, max: number): number => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        const char = seed.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash) % max;
};

// Generate time-based seed that changes every rotation period
const generateTimeSeed = (eventKey: string, rotationPeriod: number): string => {
    const periodStart = Math.floor(Date.now() / rotationPeriod) * rotationPeriod;
    return `${eventKey}-${periodStart}`;
};

export const useAvatarStore = create<AvatarSelectionState>()(
    persist(
        (set, get) => ({
            selections: {},
            rotationPeriod: DEFAULT_ROTATION_PERIOD,
            hasHydrated: false,

            setAvatarSelection: (eventKey: string, url: string, seed: string) => {
                set((state) => ({
                    selections: {
                        ...state.selections,
                        [eventKey]: {
                            url,
                            timestamp: Date.now(),
                            seed
                        }
                    }
                }));
            },

            selectOrCreateAvatarSelection: (eventKey: string, photos: Photo[], defaultUrl: string): string => {
                const state = get();
                const now = Date.now();

                const imagePhotos = photos.filter(photo => photo.type === 'image');

                if (imagePhotos.length === 0) {
                    return defaultUrl;
                }

                if (imagePhotos.length === 1) {
                    return imagePhotos[0].url;
                }

                // Avoid generating selections before hydration to prevent avatar flicker.
                if (!state.hasHydrated) {
                    // Return deterministic selection based on event key to prevent flicker
                    const timeSeed = generateTimeSeed(eventKey, state.rotationPeriod);
                    const randomIndex = seededIndex(timeSeed, imagePhotos.length);
                    return imagePhotos[randomIndex].url;
                }

                const existingSelection = state.selections[eventKey];

                if (existingSelection &&
                    (now - existingSelection.timestamp) < state.rotationPeriod) {
                    const stillExists = imagePhotos.some(photo => photo.url === existingSelection.url);
                    if (stillExists) {
                        return existingSelection.url;
                    }
                }

                const timeSeed = generateTimeSeed(eventKey, state.rotationPeriod);
                const randomIndex = seededIndex(timeSeed, imagePhotos.length);
                const selectedUrl = imagePhotos[randomIndex].url;

                state.setAvatarSelection(eventKey, selectedUrl, timeSeed);

                return selectedUrl;
            },

            clearExpiredSelections: () => {
                const state = get();
                const now = Date.now();
                const validSelections: Record<string, AvatarSelection> = {};

                Object.entries(state.selections).forEach(([eventKey, selection]) => {
                    if ((now - selection.timestamp) < state.rotationPeriod) {
                        validSelections[eventKey] = selection;
                    }
                });

                set({ selections: validSelections });
            },

            setRotationPeriod: (period: number) => {
                // Validate period (minimum 1 hour, maximum 30 days)
                const minPeriod = 60 * 60 * 1000;
                const maxPeriod = 30 * 24 * 60 * 60 * 1000;
                const validPeriod = Math.max(minPeriod, Math.min(maxPeriod, period));

                set({ rotationPeriod: validPeriod });

                // Clean up selections that might now be expired with new period
                get().clearExpiredSelections();
            }
        }),
        {
            name: 'avatar-selections',
            storage: createJSONStorage(() => localStorage),
            // Only persist selections and rotation period
            partialize: (state) => ({
                selections: state.selections,
                rotationPeriod: state.rotationPeriod
            }),
            // Enhanced onRehydrateStorage with better error handling
            onRehydrateStorage: () => (state) => {
                try {
                    if (state) {
                        state.clearExpiredSelections?.();
                        state.hasHydrated = true;
                        console.log('Avatar store hydrated successfully');
                    } else {
                        // Handle case where no stored data exists - this is normal for first-time users
                        console.log('Avatar store: no stored data, initializing fresh state');
                        // Create fresh state with hydration marked as complete
                        useAvatarStore.setState({
                            hasHydrated: true,
                            selections: {},
                            rotationPeriod: DEFAULT_ROTATION_PERIOD
                        });
                    }
                } catch (error) {
                    console.error('Avatar store hydration error:', error);
                    // Mark as hydrated to prevent infinite loading, but with empty state
                    useAvatarStore.setState({
                        hasHydrated: true,
                        selections: {},
                        rotationPeriod: DEFAULT_ROTATION_PERIOD
                    });
                }
            }
        }
    )
);

const EXPIRED_SELECTION_CLEANUP_INTERVAL_MS = 30 * 60 * 1000;

// Auto-cleanup expired selections periodically
if (typeof window !== 'undefined') {
    const cleanupInterval = setInterval(() => {
        const state = useAvatarStore.getState();
        state.clearExpiredSelections();
    }, EXPIRED_SELECTION_CLEANUP_INTERVAL_MS);

    // Clean up interval on page unload
    window.addEventListener('beforeunload', () => {
        clearInterval(cleanupInterval);
    });
}
